import React, { useCallback, useState } from 'react';
import Styled from './styled';

import { CloseIcon } from '../index';
import { useTipTapViewer } from '../../utils/TipTap';
import { EditorContent } from '@tiptap/react';

export type AlertProps = {
    /** accessibility support (alt text/aria-label, defaults to 'close') */
    ariaLabel?: string;
    /** Whether to show as banner (full-width) */
    banner?: boolean;
    /** Whether to show close icon (allow user to close alert) */
    closable?: boolean;
    /** Color of Alert (defaults to primary) */
    color?: 'primary' | 'positive' | 'secondary' | 'negative' | 'neutral';
    /** Optional subtext to display below Alert message */
    description?: string;
    /** Alert message */
    message: string;
    /** Position of the Alert (defaults to top)*/
    position?: 'top' | 'topBody' | 'bottom';
    /** Accessibility Label for the alert. (defaults to 'alert') */
    messageLabel?: string;
};

/**
 * Alert component to display a notification at the top or bottom of the parent container.
 *
 * @param {AlertProps} props - The properties to configure the Alert component.
 * @returns {JSX.Element | null} The rendered Alert component
 */
export const Alert: React.FC<AlertProps> = ({
    ariaLabel = 'close',
    banner = false,
    closable = false,
    color = 'primary',
    description,
    message,
    position = 'top',
    messageLabel = 'alert',
}) => {
    const [visible, setVisible] = useState(true);

    const viewer = useTipTapViewer({
        message: message,
        textOnly: false,
        ariaLabel: messageLabel,
    });

    const closeAlert = () => {
        setVisible(false);
    };

    if (!visible) {
        return null;
    }

    return (
        <Styled.Alert
            banner={banner}
            color={color}
            position={position}
        >
            {closable &&
                <Styled.CloseButton
                    onClick={closeAlert}
                    aria-label={ariaLabel}
                    tabIndex={0}
                    role='button'
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault(); // Prevent default scrolling for Space key
                            closeAlert();
                        }
                    }}
                >
                    <CloseIcon />
                </Styled.CloseButton>
            }
            <EditorContent editor={viewer} />
            {description && <Styled.Description>{description}</Styled.Description>}
        </Styled.Alert>
    );
};
